import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Grid,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AuthenticatedImage from './AuthenticatedImage';
import axiosInstance from '../api/axiosInstance';

const ImageAnalysisDetailsDialog = ({ open, onClose, imageId, selectionReasoning }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [details, setDetails] = useState(null);

  useEffect(() => {
    if (open && imageId) {
      fetchDetails();
    }
  }, [open, imageId]);

  const fetchDetails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔍 Fetching analysis details for image:', imageId);
      const response = await axiosInstance.get(`/embeddings/image/${imageId}/details`);
      console.log('✅ Analysis details received:', response.data);
      setDetails(response.data.embedding);
    } catch (err) {
      console.error('❌ Failed to fetch embedding details:', err);
      console.error('Error response:', err.response);
      const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to load analysis details';
      setError(`${err.response?.status || 'Error'}: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString();
  };

  const formatDuration = (ms) => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh', maxHeight: '90vh' }
      }}
    >
      <DialogTitle>
        Image Analysis Details
        {details && (
          <Typography variant="caption" display="block" color="text.secondary">
            {details.filename}
          </Typography>
        )}
        {imageId && (
          <Typography variant="caption" display="block" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
            ID: {imageId}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {details && !loading && (
          <Box>
            {/* Image Preview */}
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <AuthenticatedImage
                imageId={imageId}
                type="serve"
                alt={details.filename}
                style={{
                  maxWidth: '100%',
                  maxHeight: '60vh',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  borderRadius: 8,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              />
              <Button
                size="small"
                sx={{ mt: 1 }}
                onClick={() => window.open(`/api/images/${imageId}/serve`, '_blank')}
              >
                Open Full Image
              </Button>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Album Highlights Selection Reasoning */}
            {selectionReasoning && (
              <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.light' }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'warning.dark' }}>⭐ Album Highlights — Why Selected</Typography>
                <Typography variant="body2">{selectionReasoning}</Typography>
              </Paper>
            )}

            {/* Event Fit */}
            {(details.features?.eventFitScore != null || details.features?.eventFitReason) && (
              <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="h6" gutterBottom>Event Fit</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  {details.features?.eventFitScore != null && (
                    <Chip
                      label={`${details.features.eventFitScore}/10`}
                      color={details.features.eventFitScore >= 8 ? 'success' : details.features.eventFitScore >= 6 ? 'warning' : 'default'}
                      size="small"
                    />
                  )}
                  <Typography variant="body2" color="text.secondary">How well this image represents the album's event theme</Typography>
                </Box>
                {details.features?.eventFitReason && (
                  <Typography variant="body2">{details.features.eventFitReason}</Typography>
                )}
              </Paper>
            )}

            {/* Faces — always shown once details loaded */}
            <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
              <Typography variant="h6" gutterBottom>
                Face Recognition
                {details.faceData?.processingStatus && (
                  <Chip
                    label={details.faceData.processingStatus}
                    size="small"
                    sx={{ ml: 1, fontSize: '0.65rem' }}
                    color={details.faceData.processingStatus === 'completed' ? 'success' : details.faceData.processingStatus === 'not_available' ? 'error' : 'default'}
                  />
                )}
              </Typography>
              {!details.faceData || details.faceData.processingStatus === 'not_available' ? (
                <Typography variant="body2" color="text.secondary">Face recognition data not available for this image.</Typography>
              ) : (
                <>
                  {/* Recognized people */}
                  {details.faceData.matches && details.faceData.matches.length > 0 ? (
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>Recognized People</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {details.faceData.matches.map((m, i) => (
                          <Chip
                            key={i}
                            label={`${m.personName}${m.similarity != null ? ` (${m.similarity.toFixed(0)}%)` : ''}`}
                            color="primary"
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Box>
                  ) : (
                    details.faceData.faceDetection?.totalFaces > 0 && (
                      <Box sx={{ mb: 1.5 }}>
                        <Chip label={`⚠️ ${details.faceData.faceDetection.totalFaces} unrecognized face(s) detected`} color="warning" size="small" />
                      </Box>
                    )
                  )}
                  {/* Individual face details */}
                  {details.faceData.faceDetection?.faces && details.faceData.faceDetection.faces.length > 0 ? (
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>Detected Faces ({details.faceData.faceDetection.totalFaces})</Typography>
                      <Grid container spacing={1}>
                        {details.faceData.faceDetection.faces.map((f, i) => {
                          const topEmo = (f.emotions || []).sort((a, b) => b.confidence - a.confidence)[0];
                          const matchedPerson = details.faceData.matches?.[i]?.personName;
                          return (
                            <Grid item xs={12} sm={6} key={i}>
                              <Paper elevation={0} sx={{ p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                                <Typography variant="caption" fontWeight="bold">
                                  {matchedPerson || `Face ${i + 1}`}
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                  {f.ageRange && (
                                    <Chip label={`${f.ageRange.low}–${f.ageRange.high}y`} size="small" variant="outlined" />
                                  )}
                                  {f.gender?.value && (
                                    <Chip label={f.gender.value} size="small" variant="outlined" />
                                  )}
                                  {topEmo && (
                                    <Chip label={topEmo.type?.toLowerCase()} size="small" variant="outlined" />
                                  )}
                                  {f.smile?.value && (
                                    <Chip label="😊 smiling" size="small" color="success" variant="outlined" />
                                  )}
                                  {f.eyesOpen?.value === false && (
                                    <Chip label="⚠️ eyes closed" size="small" color="error" variant="outlined" />
                                  )}
                                  {f.eyeglasses?.value && (
                                    <Chip label="👓 glasses" size="small" variant="outlined" />
                                  )}
                                  {f.sunglasses?.value && (
                                    <Chip label="🕶️ sunglasses" size="small" variant="outlined" />
                                  )}
                                </Box>
                              </Paper>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {details.faceData.faceDetection?.processed
                        ? 'No faces detected in this image'
                        : details.faceData.matches?.length === 0
                          ? 'Face detection not yet run'
                          : 'No face details available'}
                    </Typography>
                  )}
                </>
              )}
            </Paper>

            {/* Processing Info */}
            <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
              <Typography variant="h6" gutterBottom>Processing Information</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Analysis Quality</Typography>
                  <Chip 
                    label={details.debug?.imageQuality || 'high'} 
                    color={details.debug?.imageQuality === 'high' ? 'success' : 'default'}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Processing Time</Typography>
                  <Typography variant="body1">{formatDuration(details.processingTimeMs)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Processed At</Typography>
                  <Typography variant="body1">{formatDate(details.debug?.processedAt)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Confidence</Typography>
                  <Typography variant="body1">
                    {((details.embeddingQuality?.confidence || 0.8) * 100).toFixed(0)}%
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Parsed Analysis */}
            {details.debug?.parsedDescription && (
              <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="h6" gutterBottom>Visual Analysis</Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Description</Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {details.debug.parsedDescription.description || 'N/A'}
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="body2" color="text.secondary">Scene Type</Typography>
                    <Chip label={details.debug.parsedDescription.sceneType || 'Unknown'} size="small" sx={{ mt: 0.5 }} />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="body2" color="text.secondary">Location</Typography>
                    <Chip label={details.debug.parsedDescription.locationType || 'Unknown'} size="small" sx={{ mt: 0.5 }} />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="body2" color="text.secondary">Time of Day</Typography>
                    <Chip label={details.debug.parsedDescription.timeOfDay || 'Unknown'} size="small" sx={{ mt: 0.5 }} />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="body2" color="text.secondary">Activity</Typography>
                    <Typography variant="body1">{details.debug.parsedDescription.activity || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="body2" color="text.secondary">Mood</Typography>
                    <Typography variant="body1">{details.debug.parsedDescription.mood || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="body2" color="text.secondary">Person Count</Typography>
                    <Typography variant="body1">{details.debug.parsedDescription.personCount ?? 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="body2" color="text.secondary">Quality Score</Typography>
                    <Typography variant="body1">{details.debug.parsedDescription.qualityScore || 'N/A'}/10</Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="body2" color="text.secondary">Has Smile</Typography>
                    <Chip 
                      label={details.debug.parsedDescription.hasSmile ? 'Yes' : 'No'} 
                      size="small" 
                      color={details.debug.parsedDescription.hasSmile ? 'success' : 'default'}
                      sx={{ mt: 0.5 }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="body2" color="text.secondary">Is Blurry</Typography>
                    <Chip 
                      label={details.debug.parsedDescription.isBlurry ? 'Yes' : 'No'} 
                      size="small" 
                      color={details.debug.parsedDescription.isBlurry ? 'error' : 'success'}
                      sx={{ mt: 0.5 }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="body2" color="text.secondary">Album Worthy</Typography>
                    <Chip 
                      label={details.debug.parsedDescription.isAlbumWorthy ? 'Yes' : 'No'} 
                      size="small" 
                      color={details.debug.parsedDescription.isAlbumWorthy ? 'success' : 'default'}
                      sx={{ mt: 0.5 }}
                    />
                  </Grid>
                </Grid>

                {details.debug.parsedDescription.tags && details.debug.parsedDescription.tags.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>Visual Tags</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {details.debug.parsedDescription.tags.map((tag, idx) => (
                        <Chip key={idx} label={tag} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}

                {details.debug.parsedDescription.dominantColors && details.debug.parsedDescription.dominantColors.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>Dominant Colors</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {details.debug.parsedDescription.dominantColors.map((color, idx) => (
                        <Chip key={idx} label={color} size="small" />
                      ))}
                    </Box>
                  </Box>
                )}
              </Paper>
            )}

            {/* Debug Data - Vision Prompt */}
            {details.debug?.visionPrompt && (
              <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="h6" gutterBottom>1. Vision Model Prompt</Typography>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  The prompt sent to the vision model for image analysis
                </Typography>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 1.5, 
                    bgcolor: 'grey.100', 
                    maxHeight: 300, 
                    overflow: 'auto',
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {details.debug.visionPrompt}
                </Paper>
              </Paper>
            )}

            {/* Debug Data - Raw Vision Response */}
            {details.debug?.rawVisionResponse && (
              <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="h6" gutterBottom>2. Vision Model Response</Typography>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  The raw response from the vision model
                </Typography>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 1.5, 
                    bgcolor: 'grey.100', 
                    maxHeight: 300, 
                    overflow: 'auto',
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {details.debug.rawVisionResponse}
                </Paper>
              </Paper>
            )}

            {/* Debug Data - Embedding Text */}
            {details.debug?.embeddingText && (
              <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="h6" gutterBottom>3. Final Embedding Text</Typography>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  The text used to generate the vector embedding (includes parsed analysis + metadata)
                </Typography>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 1.5, 
                    bgcolor: 'grey.100', 
                    maxHeight: 300, 
                    overflow: 'auto',
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {details.debug.embeddingText}
                </Paper>
              </Paper>
            )}

            {/* Additional Metadata - Expandable */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Additional Metadata</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {/* Metadata */}
                {details.metadata && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Image Metadata
                    </Typography>
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        p: 1.5, 
                        bgcolor: 'grey.100', 
                        maxHeight: 200, 
                        overflow: 'auto',
                        fontFamily: 'monospace',
                        fontSize: '0.75rem'
                      }}
                    >
                      <pre>{JSON.stringify(details.metadata, null, 2)}</pre>
                    </Paper>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageAnalysisDetailsDialog;
